import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UserDAO } from '../../../../core/services/dao/userDAO';
import { AdminAccess } from '../../../shared/accessAdmin';
import { ApiHelper } from '../../../../core/services/apiHelper';
import * as moment from 'moment';
import { Subscription } from 'rxjs';
import { ReportDAO } from '../../../../core/services/dao/reportDAO';
import { ContainsSubscriptions } from '../../../../core/services/interfaces/contains-subscriptions';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Report } from 'anchor-shared/lib/model/entity/report';
import { NumberFormatterService } from '../../../../core/services/number.formatter.service';
const Json2csvParser = require('json2csv').Parser;

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  start = moment().startOf('day');
  end = moment().endOf('day');
  fromDateValue = this.start.day(this.start.day() >= 3 ? -4 : -11).toDate();
  toDateValue = this.end.day(this.end.day() <= 2 ? -5 : 2).toDate();
  reportsList: Report[];
  selectedReport: Report;
  subs: Subscription[] = [];
  dateRanges: DateRange[];
  selectedRange: string;

  constructor(
    private router: Router,
    private userDao: UserDAO,
    private checkAdmin: AdminAccess,
    private apiHelper: ApiHelper,
    private reportDao: ReportDAO,
    private snackBar: MatSnackBar,
    private readonly numFormatter: NumberFormatterService,
  ) {
    this.checkAdmin.checkAdminAccess(this.userDao, this.apiHelper, this.router);
  }

  ngOnInit() {
    this.dateRanges = [
      {
        label: '7 days',
        from: moment().startOf('day').subtract(7, 'days').toDate(),
        to: moment().endOf('day').toDate(),
      },
      {
        label: '1 month',
        from: moment().startOf('day').subtract(1, 'months').toDate(),
        to: moment().endOf('day').toDate(),
      },
      {
        label: '1 year',
        from: moment().startOf('day').subtract(1, 'years').toDate(),
        to: moment().endOf('day').toDate(),
      },
      {
        label: 'Payroll',
        from: moment().day('-5').startOf('day').toDate(),
        to: moment().day('3').endOf('day').toDate(),
      },
      {
        label: 'Accounting',
        from: moment()
          .day(moment().day() >= 5 ? -1 : -8)
          .startOf('day')
          .toDate(),
        to: moment()
          .day(moment().day() >= 5 ? 5 : -2)
          .endOf('day')
          .toDate(),
      },
      {
        label: 'No Range',
        from: null,
        to: null,
      },
    ];

    this.selectedRange = '7 days';

    const reportSub = this.reportDao.getReports().subscribe((reports) => {
      this.reportsList = reports;
      this.selectedReport = this.reportsList[0];
    });
    this.subs.push(reportSub);
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }

  generateReport() {
    let from;
    let to;
    if (this.fromDateValue && this.toDateValue) {
      from = moment(this.fromDateValue).format('YYYY-MM-DD h:mm:ss');
      to = moment(this.toDateValue).format('YYYY-MM-DD h:mm:ss');
    }

    this.reportDao
      .runReport(this.selectedReport.procedureName, from, to)
      .subscribe((data) => {
        if (data && data.length > 0) {
          const jsonData = this.numFormatter.parseJsonIfString(
            JSON.stringify(data),
          );
          const json2csvParser = new Json2csvParser({ header: true });
          const csv = json2csvParser.parse(jsonData);

          let blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          if (navigator.msSaveBlob) {
            // IE 10+
            navigator.msSaveBlob(blob, this.selectedReport.procedureName);
          } else {
            let link = document.createElement('a');
            if (link.download !== undefined) {
              // feature detection
              // Browsers that support HTML5 download attribute
              let url = URL.createObjectURL(blob);
              link.setAttribute('href', url);
              link.setAttribute('download', this.selectedReport.procedureName);
              link.style.visibility = 'hidden';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }
        } else {
          this.snackBar.open(
            'No data found for the selected date range.',
            null,
            {
              duration: 5000,
              verticalPosition: 'top',
            },
          );
        }
      });
  }

  dateRangeChange(): void {
    const dateRange = this.dateRanges.find((elem) => {
      return elem.label === this.selectedRange;
    });

    this.fromDateValue = dateRange.from;
    this.toDateValue = dateRange.to;
  }

  showDateRange(dateRange: DateRange) {
    if (
      this.selectedReport &&
      !this.selectedReport.needsDateRange &&
      dateRange.label !== 'No Range'
    ) {
      return false;
    } else if (
      this.selectedReport &&
      this.selectedReport.needsDateRange &&
      dateRange.label === 'No Range'
    ) {
      return false;
    } else {
      return true;
    }
  }

  reportChange() {
    if (!this.selectedReport.needsDateRange) {
      this.selectedRange = 'No Range';
    } else {
      this.selectedRange = '7 days';
    }
  }
}

export interface DateRange {
  label: string;
  from: Date;
  to: Date;
}
